import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Layer } from '../../types/design';
import {
  GripVertical,
  Type,
  Image as ImageIcon,
  Square,
  Folder,
  Lock
} from 'lucide-react';
import { useDrag, useDrop } from 'react-dnd';
import { cn } from '../../lib/utils';

interface LayersPanelProps {
  layers: Layer[];
  setLayers: React.Dispatch<React.SetStateAction<Layer[]>>;
  selectedLayerId: string | null;
  onSelectLayer: (id: string) => void;
  onUpdateLayer: (id: string, updates: Partial<Layer>) => void;
  onClose: () => void;
  layerPositions?: Record<string, { left: number; top: number }>;
}

interface LayerItemProps {
  layer: Layer;
  index: number;
  moveLayer: (dragId: string, hoverId: string, location: 'before' | 'after') => void;
  selectedLayerId: string | null;
  onSelectLayer: (id: string) => void;
}

const LayerIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'text':
      return <Type className="w-5 h-5 text-gray-500" />;
    case 'image':
      return <ImageIcon className="w-5 h-5 text-gray-500" />;
    case 'group':
      return <Folder className="w-5 h-5 text-gray-500" />;
    case 'frame':
      return <Square className="w-5 h-5 text-gray-500" />;
    default:
      return <Square className="w-5 h-5 text-gray-500" />;
  }
};

const LayerItem: React.FC<LayerItemProps> = ({
  layer,
  moveLayer,
  selectedLayerId,
  onSelectLayer
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop({
    accept: 'LAYER',
    collect(monitor: any) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(_item: { id: string }, _monitor: any) {
      // Throttle hover logic if needed, but simple calculations are fine
    },
    drop(item: { id: string }, monitor: any) {
      if (!ref.current) return;
      const dragId = item.id;
      const hoverId = layer.id;
      if (dragId === hoverId) return;

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (hoverClientY < hoverMiddleY) {
        moveLayer(dragId, hoverId, 'before');
      } else {
        moveLayer(dragId, hoverId, 'after');
      }
    }
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'LAYER',
    item: () => ({ id: layer.id }),
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !layer.locked && layer.id !== 'root' && !(layer.id && typeof layer.id === 'string' && layer.id.startsWith('bg-')),
  });

  drag(drop(ref));

  const isSelected = selectedLayerId === layer.id;
  const isBackground = layer.locked || layer.id === 'root' || layer.name === 'Background' || (layer.id && typeof layer.id === 'string' && layer.id.startsWith('bg-'));

  return (
    <div
      ref={ref}
      onClick={() => layer.id && onSelectLayer(layer.id)}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer group transition-all relative overflow-hidden select-none mb-1",
        isDragging && "opacity-50"
      )}
      data-handler-id={handlerId}
    >
      {/* Active State Indicator */}
      <div 
          className="app-selector-indicator" 
          style={{ '--background-width': isSelected ? '100%' : '0%' } as React.CSSProperties} 
      />

      <div className={cn(
        "text-gray-300 cursor-grab active:cursor-grabbing relative z-10",
        isBackground && "invisible"
      )}>
        <GripVertical className="w-5 h-5" />
      </div>

      <div className="shrink-0 relative z-10">
        <LayerIcon type={layer.type || 'frame'} />
      </div>

      <div className="flex-1 min-w-0 relative z-10">
        <span className={cn(
          "block text-sm font-medium truncate transition-colors duration-400",
          isSelected ? "app-active-state" : "text-gray-700"
        )}>
          {layer.name || layer.type || 'Unnamed Layer'}
        </span>
      </div>

      {(layer.locked || isBackground) && (
        <Lock className="w-3 h-3 text-gray-400 relative z-10" />
      )}
    </div>
  );
};

export const LayersPanel: React.FC<LayersPanelProps> = ({
  layers,
  setLayers,
  selectedLayerId,
  onSelectLayer,
  layerPositions
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'overlapping'>('all');

  // Helper to flatten the tree into a Paint Order list (Back to Front)
  const getPaintOrderLayers = useCallback((nodes: Layer[]): Layer[] => {
    let result: Layer[] = [];
    if (!nodes || !Array.isArray(nodes)) return result;

    nodes.forEach(node => {
      if (!node) return;
      // Add node (Parent/Background)
      result.push(node);
      // Add children
      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        result = result.concat(getPaintOrderLayers(node.children));
      }
    });
    return result;
  }, []);

  const flattenedLayers = useMemo(() => {
    return getPaintOrderLayers(layers).filter(l => l && l.id);
  }, [layers, getPaintOrderLayers]);

  // Display Layers: Front to Back (Reverse Paint Order)
  const displayLayers = useMemo(() => {
    // ALWAYS start with full list for 'All' tab
    const reversed = [...flattenedLayers].reverse();

    // Only filter for overlapping tab
    if (activeTab === 'overlapping' && selectedLayerId) {
      const selectedLayer = flattenedLayers.find(l => l.id === selectedLayerId);
      if (!selectedLayer) {
        // Fallback
        return reversed;
      }

      const getRect = (id: string) => {
        if (!id) return null;
        const el = document.getElementById(id);
        if (el) return el.getBoundingClientRect();
        if (layerPositions && layerPositions[id]) {
          return {
            left: layerPositions[id].left,
            top: layerPositions[id].top,
            right: layerPositions[id].left + 100,
            bottom: layerPositions[id].top + 100,
            width: 100, height: 100
          };
        }
        return null;
      };

      const selRect = getRect(selectedLayerId);
      if (!selRect) return reversed;

      return reversed.filter(l => {
        if (!l || !l.id) return false;
        if (l.id === selectedLayerId) return true;
        const rect = getRect(l.id);
        if (!rect) return false;

        return !(
          rect.right < selRect.left ||
          rect.left > selRect.right ||
          rect.bottom < selRect.top ||
          rect.top > selRect.bottom
        );
      });
    }

    // Default to 'All' behavior which is strictly all elements
    return reversed;
  }, [flattenedLayers, activeTab, selectedLayerId, layerPositions]);

  // Handle Layer Move (Reparenting if necessary)
  const moveLayer = useCallback((dragId: string, targetId: string, location: 'before' | 'after') => {
    setLayers((prevLayers: Layer[]) => {
      const cloned = JSON.parse(JSON.stringify(prevLayers));

      let draggedLayer: Layer | null = null;
      const removeRecursive = (list: Layer[]): Layer[] => {
        return list.filter(item => {
          if (item.id === dragId) {
            draggedLayer = item;
            return false;
          }
          if (item.children) {
            item.children = removeRecursive(item.children);
          }
          return true;
        });
      };

      const layersWithoutDrag = removeRecursive(cloned);
      if (!draggedLayer) return prevLayers;

      const insertRecursive = (list: Layer[]): boolean => {
        const targetIndex = list.findIndex(l => l.id === targetId);
        if (targetIndex !== -1) {
          if (location === 'before') {
            list.splice(targetIndex + 1, 0, draggedLayer!);
          } else {
            list.splice(targetIndex, 0, draggedLayer!);
          }
          return true;
        }

        for (const item of list) {
          if (item.children) {
            if (insertRecursive(item.children)) return true;
          }
        }
        return false;
      };

      insertRecursive(layersWithoutDrag);

      return layersWithoutDrag;
    });
  }, [setLayers]);

  // Force 'All' tab if selection is cleared while in overlapping mode
  useEffect(() => {
    if (!selectedLayerId && activeTab === 'overlapping') {
      setActiveTab('all');
    }
  }, [selectedLayerId, activeTab]);

  return (
    <div className="flex flex-col bg-white w-full h-full text-gray-900 font-sans">
      {/* Tabs */}
      <div className="flex items-center px-4 pt-4 border-b border-gray-100 shrink-0 gap-6">
        <button
          onClick={() => setActiveTab('all')}
          className={cn(
            "text-sm font-medium pb-3 pt-1 transition-all relative group",
            activeTab === 'all'
              ? "app-active-state"
              : "text-gray-500 hover:text-gray-800"
          )}
        >
          {/* Active Underline Indicator */}
          <div 
              className="app-tab-indicator" 
              style={{ '--background-width': activeTab === 'all' ? '100%' : '0%' } as React.CSSProperties} 
          />
          All
        </button>
        <button
          onClick={() => setActiveTab('overlapping')}
          className={cn(
            "text-sm font-medium pb-3 pt-1 transition-all relative group",
            activeTab === 'overlapping'
              ? "app-active-state"
              : "text-gray-500 hover:text-gray-800",
            !selectedLayerId && "opacity-50 cursor-not-allowed"
          )}
          disabled={!selectedLayerId}
          title={!selectedLayerId ? "Select an element to see overlaps" : "Show overlapping elements"}
        >
          {/* Active Underline Indicator */}
          <div 
              className="app-tab-indicator" 
              style={{ '--background-width': activeTab === 'overlapping' ? '100%' : '0%' } as React.CSSProperties} 
          />
          Overlapping
        </button>
      </div>

      {/* List Content */}
      <div className="flex-1 overflow-y-auto px-2.5 py-2 custom-scrollbar space-y-0.5">
        {displayLayers.length > 0 ? (
          <div className="flex flex-col">
            {displayLayers.map((layer, index) => (
              <LayerItem
                key={`layers-panel-item-${layer.id || index}-${index}`}
                index={index}
                layer={layer}
                moveLayer={moveLayer}
                selectedLayerId={selectedLayerId}
                onSelectLayer={onSelectLayer}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center px-6">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
              {activeTab === 'overlapping' ? (
                <Square className="w-6 h-6 rotate-45" />
              ) : (
                <Folder className="w-6 h-6" />
              )}
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">
              {activeTab === 'overlapping' ? "No overlaps found" : "No layers yet"}
            </p>
            <p className="text-xs text-gray-500 leading-relaxed max-w-[180px]">
              {activeTab === 'overlapping'
                ? "We couldn't find any elements directly under your selection."
                : "Your layers will appear here once you add elements to your design."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

